import { z } from 'zod';

// src/precedence/enums.generated.ts
var CatalogScope = {
  ENTITY: "entity",
  ADDRESS: "address",
  TRANSACTION: "transaction",
  EVENT: "event",
  INTERNAL_TX: "internal_tx"
};
var CatalogLifecycle = {
  VERIFIED: "verified",
  CANDIDATE: "candidate",
  DEPRECATED: "deprecated",
  QUARANTINED: "quarantined"
};
var MemberTier = {
  CORE: "core",
  DERIVED: "derived"
};
var CatalogKind = {
  TAG: "tag",
  LABEL: "label",
  GROUP: "group",
  ENTITY: "entity"
};
var Severity = {
  INFO: "info",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical"
};
var RiskLevel = {
  SAFE: "safe",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  SEVERE: "severe"
};

// src/precedence/tier.ts
var Tier = {
  USER_OVERRIDE: "user_override",
  USER_BASE: "user_base",
  /** Tenant scope, verified (wire value kept as `customer_override`). */
  CUSTOMER_OVERRIDE: "customer_override",
  /** Tenant scope, unverified (wire value kept as `customer_base`). */
  CUSTOMER_BASE: "customer_base",
  /** Org-wide scope, verified — below tenant, above default. */
  ORG_OVERRIDE: "org_override",
  /** Org-wide scope, unverified. */
  ORG_BASE: "org_base",
  DEFAULT: "default",
  ENTITY_INHERITED: "entity_inherited",
  PROVIDER_CANDIDATE: "provider_candidate"
};
var TENANT_OVERRIDE = Tier.CUSTOMER_OVERRIDE;
var TENANT_BASE = Tier.CUSTOMER_BASE;
var TIER_WEIGHTS = {
  [Tier.USER_OVERRIDE]: 90,
  [Tier.USER_BASE]: 80,
  [Tier.CUSTOMER_OVERRIDE]: 70,
  [Tier.CUSTOMER_BASE]: 60,
  [Tier.ORG_OVERRIDE]: 50,
  [Tier.ORG_BASE]: 40,
  [Tier.DEFAULT]: 30,
  [Tier.ENTITY_INHERITED]: 20,
  [Tier.PROVIDER_CANDIDATE]: 10
};
function tierWeight(tier) {
  return TIER_WEIGHTS[tier] ?? TIER_WEIGHTS[Tier.PROVIDER_CANDIDATE];
}
function compareTiers(a, b) {
  const wa = tierWeight(a);
  const wb = tierWeight(b);
  if (wa < wb) return -1;
  if (wa > wb) return 1;
  return 0;
}
function deriveTier(input) {
  const { hasCustomerScope, isVerified, inheritedFromEntity } = input;
  const isUserScoped = input.isUserScoped ?? false;
  const isOrgScoped = input.isOrgScoped ?? false;
  if (isUserScoped && isVerified) return Tier.USER_OVERRIDE;
  if (isUserScoped) return Tier.USER_BASE;
  if (hasCustomerScope && isVerified) return Tier.CUSTOMER_OVERRIDE;
  if (hasCustomerScope) return Tier.CUSTOMER_BASE;
  if (isOrgScoped && isVerified) return Tier.ORG_OVERRIDE;
  if (isOrgScoped) return Tier.ORG_BASE;
  if (inheritedFromEntity) return Tier.ENTITY_INHERITED;
  if (isVerified) return Tier.DEFAULT;
  return Tier.PROVIDER_CANDIDATE;
}
function mergeByPrecedence(hits, keyFn, tierFn) {
  const chosen = /* @__PURE__ */ new Map();
  for (const hit of hits) {
    const k = keyFn(hit);
    const existing = chosen.get(k);
    if (existing === void 0 || tierWeight(tierFn(hit)) > tierWeight(tierFn(existing))) {
      chosen.set(k, hit);
    }
  }
  return [...chosen.values()];
}
function resolve(hits, opts) {
  const { keyFn, deriveTierFn } = opts;
  const tenantContext = opts.tenantContext ?? null;
  const chainId = opts.chainId ?? null;
  return mergeByPrecedence(hits, keyFn, (h) => deriveTierFn(h, tenantContext, chainId));
}

// src/precedence/overrides.ts
function isSuppressed(lifecycle) {
  return lifecycle === CatalogLifecycle.QUARANTINED;
}
function filterSuppressed(hits, lifecycleFn) {
  return [...hits].filter((hit) => !isSuppressed(lifecycleFn(hit)));
}
function applyOverrides(base, overrides, opts) {
  const all = [...base, ...overrides];
  const lifecycleFn = opts.lifecycleFn;
  const winners = mergeByPrecedence(all, opts.keyFn, opts.tierFn);
  if (!lifecycleFn) return winners;
  return winners.filter((hit) => !isSuppressed(lifecycleFn(hit)));
}

// src/precedence/risk-ladder.generated.json
var risk_ladder_generated_default = {
  bands: {
    high: 60,
    low: 20,
    medium: 40,
    severe: 85
  },
  level_by_severity: {
    critical: "severe",
    high: "high",
    low: "low",
    medium: "medium"
  },
  scale_top: 100,
  score_by_level: {
    high: 72.5,
    low: 30,
    medium: 50,
    safe: 10,
    severe: 92.5
  },
  score_by_severity: {
    critical: 92.5,
    high: 72.5,
    low: 30,
    medium: 50
  },
  severity_by_level: {
    high: "high",
    low: "low",
    medium: "medium",
    severe: "critical"
  }
};

// src/precedence/risk-ladder.ts
var LadderSchema = z.object({
  scale_top: z.number(),
  bands: z.object({
    severe: z.number(),
    high: z.number(),
    medium: z.number(),
    low: z.number()
  }),
  level_by_severity: z.record(z.string(), z.string()),
  severity_by_level: z.record(z.string(), z.string()),
  score_by_level: z.record(z.string(), z.number()),
  score_by_severity: z.record(z.string(), z.number())
});
var SNAPSHOT = LadderSchema.parse(risk_ladder_generated_default);
var RISK_SCORE_SCALE_TOP = SNAPSHOT.scale_top;
var RISK_SCORE_BANDS = Object.freeze({ ...SNAPSHOT.bands });
var LEVEL_BY_SEVERITY = Object.freeze(
  SNAPSHOT.level_by_severity
);
var SEVERITY_BY_LEVEL = Object.freeze(
  SNAPSHOT.severity_by_level
);
var SCORE_BY_SEVERITY = Object.freeze(
  SNAPSHOT.score_by_severity
);
var SCORE_BY_LEVEL = Object.freeze(SNAPSHOT.score_by_level);

// src/precedence/risk.ts
var RISK_LEVEL_ORDER = [
  RiskLevel.SAFE,
  RiskLevel.LOW,
  RiskLevel.MEDIUM,
  RiskLevel.HIGH,
  RiskLevel.SEVERE
];
var RISK_LEVEL_RANK = Object.freeze(
  Object.fromEntries(RISK_LEVEL_ORDER.map((level, i) => [level, i]))
);
var RISK_VALUES = new Set(RISK_LEVEL_ORDER);
function coerceRisk(value) {
  const s = typeof value === "string" ? value.toLowerCase() : "";
  return RISK_VALUES.has(s) ? s : void 0;
}
function worstBy(items, map, levelOf) {
  let worst;
  let worstRank = -1;
  for (const item of items) {
    const mapped = map(item);
    if (mapped === void 0) continue;
    const rank = RISK_LEVEL_RANK[levelOf(mapped)] ?? 0;
    if (rank > worstRank) {
      worst = mapped;
      worstRank = rank;
    }
  }
  return worst;
}
function worstRisk(values) {
  return worstBy(values, coerceRisk, (level) => level);
}
var DEFAULT_RISK_SCORE_BANDS = RISK_SCORE_BANDS;
function riskLevelFromScore(score, bands = DEFAULT_RISK_SCORE_BANDS) {
  if (score == null || !Number.isFinite(score)) return void 0;
  if (score >= bands.severe) return RiskLevel.SEVERE;
  if (score >= bands.high) return RiskLevel.HIGH;
  if (score >= bands.medium) return RiskLevel.MEDIUM;
  if (score >= bands.low) return RiskLevel.LOW;
  return RiskLevel.SAFE;
}

export { CatalogKind, CatalogLifecycle, CatalogScope, DEFAULT_RISK_SCORE_BANDS, LEVEL_BY_SEVERITY, MemberTier, RISK_LEVEL_ORDER, RISK_LEVEL_RANK, RISK_SCORE_BANDS, RISK_SCORE_SCALE_TOP, RiskLevel, SCORE_BY_LEVEL, SCORE_BY_SEVERITY, SEVERITY_BY_LEVEL, Severity, TENANT_BASE, TENANT_OVERRIDE, TIER_WEIGHTS, Tier, applyOverrides, coerceRisk, compareTiers, deriveTier, filterSuppressed, isSuppressed, mergeByPrecedence, resolve, riskLevelFromScore, tierWeight, worstBy, worstRisk };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map